
import React, { useState } from 'react';
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import LoadingSpinner from "@/components/ui/LoadingSpinner";
import { Invite } from './useInvites';

interface InviteListProps {
  invites: Invite[];
  isLoading: boolean;
  handleRevokeInvite: (code: string) => Promise<void>;
}

const InviteList: React.FC<InviteListProps> = ({ invites, isLoading, handleRevokeInvite }) => {
  const [revokeInProgress, setRevokeInProgress] = useState<Record<string, boolean>>({});
  
  const handleRevoke = async (code: string) => {
    setRevokeInProgress(prev => ({ ...prev, [code]: true }));
    await handleRevokeInvite(code);
    setRevokeInProgress(prev => ({ ...prev, [code]: false }));
  };

  const getInviteStatusBadge = (status: string) => {
    switch (status) {
      case 'pending':
        return <Badge variant="outline" className="bg-yellow-100 text-yellow-800">Pending</Badge>;
      case 'accepted':
        return <Badge variant="outline" className="bg-green-100 text-green-800">Accepted</Badge>;
      case 'expired':
        return <Badge variant="outline" className="bg-gray-100 text-gray-800">Expired</Badge>;
      case 'revoked':
        return <Badge variant="outline" className="bg-red-100 text-red-800">Revoked</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  if (invites.length === 0) {
    return (
      <div className="p-8 text-center text-muted-foreground">
        <div className="mb-3 text-4xl">📮</div>
        <h3 className="text-lg font-medium mb-2">No invites created yet</h3>
        <p className="mb-4 text-sm">Create your first invite to get started with inviting users</p>
      </div>
    );
  }

  return (
    <div className="divide-y">
      {invites.map((invite) => (
        <div key={invite.code} className="grid grid-cols-12 p-3 text-sm items-center">
          <div className="col-span-3 truncate">{invite.email}</div>
          <div className="col-span-3 font-mono text-xs">{invite.code}</div>
          <div className="col-span-2">{getInviteStatusBadge(invite.status)}</div>
          <div className="col-span-2 text-muted-foreground">
            {new Date(invite.created_at).toLocaleDateString()}
          </div>
          <div className="col-span-2">
            {invite.status === 'pending' && (
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => handleRevoke(invite.code)}
                disabled={isLoading || revokeInProgress[invite.code]}
              >
                {revokeInProgress[invite.code] ? (
                  <LoadingSpinner size={3} className="mr-1" />
                ) : null}
                Revoke
              </Button>
            )}
          </div>
        </div>
      ))}
    </div>
  );
};

export default InviteList;
